const CONTEXT_LIMIT = 200_000;
const WARNING_THRESHOLD = 0.8;
const CRITICAL_THRESHOLD = 0.95;

interface ContextUsageBarProps {
  inputTokens: number;
}

export function ContextUsageBar({ inputTokens }: ContextUsageBarProps) {
  if (inputTokens <= 0) return null;

  const ratio = Math.min(inputTokens / CONTEXT_LIMIT, 1);
  const percent = Math.round(ratio * 100);

  let barColor = "bg-accent";
  let textColor = "text-text-tertiary";
  if (ratio >= CRITICAL_THRESHOLD) {
    barColor = "bg-error";
    textColor = "text-error";
  } else if (ratio >= WARNING_THRESHOLD) {
    barColor = "bg-amber-500";
    textColor = "text-amber-600";
  }

  return (
    <div
      className="flex items-center gap-1.5"
      title={`${inputTokens.toLocaleString()} / ${CONTEXT_LIMIT.toLocaleString()} tokens`}
    >
      <div className="w-16 h-1.5 rounded-full bg-surface-secondary overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className={`text-[10px] tabular-nums ${textColor}`}>{percent}%</span>
    </div>
  );
}

interface ContextWarningBannerProps {
  inputTokens: number;
  onCompact?: () => void;
  isCompacting?: boolean;
}

export function ContextWarningBanner({
  inputTokens,
  onCompact,
  isCompacting,
}: ContextWarningBannerProps) {
  const ratio = inputTokens / CONTEXT_LIMIT;

  if (ratio >= CRITICAL_THRESHOLD) {
    return (
      <div className="px-3 py-1.5 bg-error/10 border-b border-error/20 text-xs text-error flex items-center justify-between gap-2">
        <span>Thread is near capacity — start a new thread to continue</span>
        {onCompact && (
          <button
            type="button"
            onClick={onCompact}
            disabled={isCompacting}
            className="shrink-0 px-2 py-0.5 rounded bg-error/20 hover:bg-error/30 text-error text-xs font-medium disabled:opacity-50"
          >
            {isCompacting ? "Compacting…" : "Compact thread"}
          </button>
        )}
      </div>
    );
  }

  if (ratio >= WARNING_THRESHOLD) {
    return (
      <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/20 text-xs text-amber-600 flex items-center justify-between gap-2">
        <span>Context is filling up — consider compacting or starting a new thread</span>
        {onCompact && (
          <button
            type="button"
            onClick={onCompact}
            disabled={isCompacting}
            className="shrink-0 px-2 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-700 text-xs font-medium disabled:opacity-50"
          >
            {isCompacting ? "Compacting…" : "Compact thread"}
          </button>
        )}
      </div>
    );
  }

  return null;
}
